import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, query, where, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { CollectionPaths } from 'shared_schemas';
import { MatchList } from '../components/events/MatchList';
import QRCode from 'react-qr-code';

export default function PlayerEventDashboard() {
    const { eventId } = useParams<{ eventId: string }>();
    const { user, userDoc } = useAuth();
    const [event, setEvent] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'matches' | 'scouting' | 'checkin'>('checkin');
    const [checkingIn, setCheckingIn] = useState(false);

    // Check if player is checked in (mock logic for now, would query attendance)
    const [isCheckedIn, setIsCheckedIn] = useState(false);

    useEffect(() => {
        async function loadData() {
            if (!eventId) return;
            try {
                const eventDoc = await getDoc(doc(db, CollectionPaths.events, eventId));
                if (eventDoc.exists()) {
                    setEvent({ id: eventDoc.id, ...eventDoc.data() });
                }

                // Check attendance
                if (userDoc?.linkedPlayerId && userDoc?.schoolId) {
                    const q = query(
                        collection(db, CollectionPaths.attendance(userDoc.schoolId)),
                        where('eventId', '==', eventId),
                        where('studentId', '==', userDoc.linkedPlayerId)
                    );
                    const snap = await getDocs(q);
                    if (!snap.empty) setIsCheckedIn(true);
                }
            } catch (err) {
                console.error("Error loading event", err);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, [eventId, userDoc, isCheckedIn]);

    const handleSelfCheckIn = async () => {
        if (!userDoc?.linkedPlayerId || !userDoc?.schoolId || !eventId || !event) return;
        setCheckingIn(true);
        try {
            // Create Attendance Record
            const attendanceRef = doc(collection(db, CollectionPaths.attendance(userDoc.schoolId)));
            await setDoc(attendanceRef, {
                id: attendanceRef.id,
                date: serverTimestamp(),
                eventType: event.type || 'competition',
                eventName: event.title,
                studentId: userDoc.linkedPlayerId,
                status: 'present',
                checkInMethod: 'app',
                checkInTime: serverTimestamp(),
                recordedBy: user?.uid,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            // Optimistic update
            setIsCheckedIn(true);
            alert("Checked in successfully!");
        } catch (err) {
            console.error("Check-in failed", err);
            alert("Failed to check in. Please try again or see a coach.");
        } finally {
            setCheckingIn(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-secondary-400">Loading...</div>;
    if (!event) return <div className="p-8 text-center text-secondary-400">Event not found</div>;

    return (
        <div className="max-w-md mx-auto min-h-screen bg-secondary-900 pb-20">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-secondary-900 border-b border-secondary-800 p-4 flex items-center gap-3 shadow-lg">
                <Link to="/app/dashboard" className="text-secondary-400 text-lg">←</Link>
                <div>
                    <h1 className="text-white font-bold text-lg leading-tight">{event.title}</h1>
                    <p className="text-secondary-400 text-xs">{event.date} • {event.location}</p>
                </div>
            </div>

            {/* Check-in Status Banner */}
            <div className={`p-3 ${isCheckedIn ? 'bg-green-500/10 border-b border-green-500/20' : 'bg-amber-500/10 border-b border-amber-500/20'}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${isCheckedIn ? 'bg-green-500' : 'bg-amber-500'}`} />
                        <span className={`text-sm font-medium ${isCheckedIn ? 'text-green-400' : 'text-amber-400'}`}>
                            {isCheckedIn ? 'Checked In' : 'Not Checked In'}
                        </span>
                    </div>
                    {!isCheckedIn && (
                        <button
                            onClick={handleSelfCheckIn}
                            disabled={checkingIn}
                            className="text-xs bg-amber-500 text-black font-bold px-3 py-1.5 rounded-full shadow-lg hover:shadow-amber-500/50 transition-all hover:scale-105"
                        >
                            {checkingIn ? '...' : 'Check In Now'}
                        </button>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <div className="flex border-b border-secondary-800 bg-secondary-900">
                <button
                    onClick={() => setActiveTab('matches')}
                    className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'matches' ? 'border-primary-500 text-primary-500' : 'border-transparent text-secondary-400 hover:text-white'}`}
                >
                    Matches
                </button>
                <button
                    onClick={() => setActiveTab('scouting')}
                    className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'scouting' ? 'border-primary-500 text-primary-500' : 'border-transparent text-secondary-400 hover:text-white'}`}
                >
                    Scouting
                </button>
                <button
                    onClick={() => setActiveTab('checkin')}
                    className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'checkin' ? 'border-primary-500 text-primary-500' : 'border-transparent text-secondary-400 hover:text-white'}`}
                >
                    Pass
                </button>
            </div>

            {/* Content */}
            <div className="p-4">
                {activeTab === 'matches' && (
                    <MatchList
                        eventId={eventId!}
                        startggEventId={event.startggId}
                        isAdmin={false}
                        playerId={userDoc?.linkedPlayerId} // Filter to show my matches first (logic inside MatchList)
                    />
                )}

                {activeTab === 'scouting' && (
                    <div className="space-y-4">
                        <div className="card p-4 text-center">
                            <p className="text-secondary-400 mb-4">Search for opponents to see their habits and weaknesses.</p>
                            <Link to="/app/scouting" className="btn-primary w-full block">
                                Open Scout Database
                            </Link>
                        </div>
                    </div>
                )}

                {activeTab === 'checkin' && (
                    <div className="space-y-6">
                        <div className="card bg-white flex flex-col items-center p-8 rounded-xl">
                            <QRCode value={`${window.location.origin}/checkin/${eventId}?uid=${user?.uid}`} size={200} />
                            <p className="text-gray-500 mt-4 text-sm font-mono text-center">
                                {userDoc?.displayName}<br />
                                {userDoc?.linkedPlayerName}
                            </p>
                        </div>
                        <div className="text-center text-sm text-secondary-500">
                            {isCheckedIn ?
                                <span className="text-green-500 font-bold">You are checked in! ✅</span> :
                                <span>Show this QR code to your coach or <button onClick={handleSelfCheckIn} className="text-primary-400 underline">Check In Here</button></span>
                            }
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
